<script lang="ts" setup>
import appStore from '@/store/AppStore'
import formStore from '@/store/FormStore'
import userStore from '@/store/UserStore'
import { CustomAccessoryArticleType, HpStep, KeyNumType, ValidationRuleType } from '@/helper/Types'
import ProjectController from '@/controllers/ProjectController'
import FormController from '@/controllers/FormController'
import { StepsController } from '@/controllers/StepsController'
import { camelize, ValidatorType } from '@/helper/ValidateDirective'
import PriceInput from '@/components/PriceInput.vue'
import PriceCalculator from '@/components/steps/summary/PriceCalculator.vue'
import TotalPrice from '@/components/steps/summary/TotalPrice.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import { computed } from 'vue'
import { calculateDisplayPrice, copyToClipboard, formatPrice } from '@/helper/tools'

const props = withDefaults(
  defineProps<{
    title: string
    products: CustomAccessoryArticleType[]
    totalPriceLabel: string
    totalPrice?: number
    validator: ValidatorType
    enableCustomPrices: boolean
  }>(),
  {
    enableCustomPrices: () => false,
    title: () => '',
    products: () => [],
    totalPriceLabel: '',
    totalPrice: undefined,
    validator: () => ({})
  }
)

const customPrices = computed((): KeyNumType => formStore.data.customPrices as KeyNumType)

const sortedProducts = computed((): CustomAccessoryArticleType[] => sortAccessories(props.products))

function sortAccessories(products: CustomAccessoryArticleType[]): CustomAccessoryArticleType[] {
  return products.sort((a: CustomAccessoryArticleType, b: CustomAccessoryArticleType) => a.name.localeCompare(b.name))
}

function calculatorConfirmed(id: string, newPrice: number) {
  (formStore.data.customPrices as KeyNumType)[id] = newPrice
  customPriceBlurred()
}

async function customPriceBlurred() {
  if (appStore.isLoading) {
    return
  }
  appStore.isLoading = true
  await ProjectController.saveProject()
  FormController.loadFormData()
  appStore.isLoading = false
}

defineExpose({
  validator: props.validator
})
</script>

<template>
  <section class="section--accessories">
    <div v-if="products.length" class="steps-table__header">
      <SectionHeadline :translation-key="title" class="form__title" />
      <button class="item__modify btn is--raw icon-text" @click="StepsController.jumpToStep(HpStep.ACCESSORIES)">
        <i class="ic-edit" />{{ $t('step_summary.btn_modify') }}
      </button>
    </div>

    <div
      v-if="products.length"
      :class="{ 'is--custom-price': enableCustomPrices }"
      class="summary card is--accessories is--custom-article"
    >
      <div class="fTable is--products-table">
        <div class="fTable__tr">
          <div class="fTable__th column is--product">
            <span>{{ $t('step_summary.table_accessories_product') }}</span>
          </div>
          <div class="fTable__th column is--quantity">
            <span>{{ $t('step_summary.table_accessories_quantity') }}</span>
          </div>
          <div v-if="!userStore.hidePrices" class="fTable__th column is--price">
            <span>{{ $t('step_summary.table_accessories_price') }}</span>
          </div>
        </div>

        <div
          v-for="(article, i) in sortedProducts"
          :key="i"
          :data-id="article.id"
          class="fTable__select"
          @click.alt.prevent="article.id && copyToClipboard(article.id)"
        >
          <template v-if="article">
            <div class="fTable__tr has--td">
              <div class="fTable__td column is--product">
                <div class="fTable__thumbnail">
                  <div v-if="article.pictureUrl" class="fTable__image-box image-box">
                    <div :class="!article.pictureUrl && 'fallback-bg'" class="fTable__image image-box__container">
                      <img v-if="article.pictureUrl" :alt="article.name" :src="article.pictureUrl" loading="lazy">
                    </div>
                  </div>
                  <div v-else class="fTable__image-box image-box has--no-bg">
                    <div class="fTable__image image-box__container">
                      <i :class="`ic-ca-${article.group}`" />
                    </div>
                  </div>
                </div>
                <div class="fTable__product-infos">
                  <span>{{ article.name }}</span>
                </div>
              </div>
              <div class="fTable__td column is--quantity">
                <span>{{ article.quantity }}</span>
              </div>
              <div v-if="!userStore.hidePrices" class="fTable__td column is--price">
                <div v-if="enableCustomPrices" class="item__custom-price">
                  <div class="custom-price-info">
                    {{ $t('step_summary.custom_price_legal_info') }}
                  </div>
                  <div class="custom-price__input-container">
                    <PriceInput
                      :id="'custom-price-' + article.id"
                      v-model:price="customPrices[article.id]"
                      v-validate="ValidationRuleType.POSITIVE_ZERO_DECIMAL"
                      :disabled="appStore.isLoading"
                      :error="validator[camelize('custom-price-' + article.id)]?.error"
                      :original-price="article.price * article?.quantity || 0"
                      @blur="customPriceBlurred"
                    />
                    <PriceCalculator
                      :id="article.id"
                      :original-price="article.price * article.quantity || 0"
                      @confirm="newPrice => calculatorConfirmed(article.id, newPrice)"
                    />
                  </div>

                  <div class="item__custom-price-infos">
                    <div>
                      {{ $t('step_summary.list_price') }}:
                      {{ formatPrice(article.price * article.quantity || 0) }}
                    </div>
                    <div>&nbsp;{{ $t('step_summary.table_vat_excluded') }}</div>
                  </div>
                </div>
                <span v-else>{{
                  article.price ? calculateDisplayPrice(article.price, article.quantity) : '-'
                }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <TotalPrice
        v-if="!userStore.hidePrices && totalPrice"
        :label="totalPriceLabel"
        :price="totalPrice"
        class="total-price is--accessory"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

$maxBadgeWidth: 230;
$maxBadgeWidthCustomPrice: 270;
$summaryPadding: 32;
$summaryPaddingMobile: 32;

.custom-price-info {
  padding-bottom: 0;
}

.fTable {
  .column__controls {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: rem-calc(102);
  }
}

.item__modify {
  white-space: nowrap;
}

.card {
  overflow: visible;
}

.summary.is--accessories {
  @include media('<desktop') {
    padding: 1rem;
  }

  &.is--custom-price {
    .fTable {
      .column {
        &.is--price {
          flex-basis: rem-calc(240);
          min-width: rem-calc(240);
          max-width: rem-calc(240);
          flex-grow: 0;
          flex-shrink: 0;
          overflow: visible;
        }
      }
    }
  }

  .fTable {
    .column {
      padding-top: rem-calc(12);
      padding-bottom: rem-calc(12);

      span {
        font-size: rem-calc(16);
      }

      &.is--product {
        padding-left: rem-calc(16);
        flex-basis: 48%;
        min-width: 48%;
        max-width: 48%;
        flex-shrink: 0;
        flex-grow: 0;
        overflow: visible;

        &.fTable__th {
          padding-left: rem-calc(46);
        }
      }

      @include media('<tablet') {
        .is--component-spec {
          display: flex;
          flex-direction: column;
        }
      }

      &.is--price {
        $priceWidth: 22%;
        flex-basis: $priceWidth;
        min-width: $priceWidth;
        max-width: $priceWidth;
        justify-content: flex-end;
        padding-right: rem-calc(16);

        @include media('<desktop') {
          justify-content: center;
        }

        @include media('<tablet') {
          flex-basis: 30%;
          min-width: 30%;
          max-width: 30%;

          span {
            overflow: visible;
          }
        }
      }

      &.is--quantity {
        justify-content: center;
      }

      &.fTable__th {
        span {
          font-size: rem-calc(14);
        }
      }
    }

    &__product-infos {
      span {
        white-space: normal;
      }
    }

    &__th {
      border-bottom: 1px solid $borderColorLight;

      @include media('<desktop') {
        height: rem-calc(52);

        span {
          line-height: rem-calc(28);
        }
      }
    }

    &__td {
      border-bottom: 1px solid $borderColorLight;
    }

    &__thumbnail {
      margin-right: rem-calc(12);
    }
  }
}

.summary.is--custom-article {
  .image-box__container {
    background: transparent;

    [class^='ic-'],
    [class*=' ic-'] {
      font-size: rem-calc(56);

      @include themify($themes) {
        color: themed('primaryColor');
      }
    }
  }
}
</style>
